import { useEffect, useRef, useState } from 'react';

import { useSocketContext } from 'context/SocketContext';

function useSocketEvent(eventName, emitPayload, options) {
  const { onMessage = () => {}, onlyListen = false } = options || {};

  const { subscribe, unsubscribe } = eventName;
  const { isConnected, socket } = useSocketContext();

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Always keep handlerRef current so onMessage never goes stale
  const handlerRef = useRef(null);
  handlerRef.current = (payload) => {
    onMessage(payload);
    setData(payload);
    setIsLoading(false);
  };

  // Register the listener once per socket instance — stays alive through disconnects
  useEffect(() => {
    if (!socket) return;

    const handler = (payload) => handlerRef.current?.(payload);

    socket.on(subscribe, handler);

    return () => {
      setIsLoading(true);
      setData(null);
      socket.off(subscribe, handler);
      if (unsubscribe && unsubscribe !== subscribe) {
        socket.emit(unsubscribe, emitPayload);
      }
    };
  }, [socket, subscribe, unsubscribe]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-emit the subscribe event after every (re)connect for rooms/channels
  useEffect(() => {
    if (!socket || !isConnected || onlyListen) return;

    if (emitPayload) {
      socket.emit(subscribe, emitPayload);
    } else {
      socket.emit(subscribe);
    }
  }, [socket, isConnected, onlyListen, subscribe, emitPayload]);

  return { data, isLoading };
}

export default useSocketEvent;
